<template>
  <div class="ss-root">
    <b-container fluid class="px-4 py-4">

      <h1 class="h2 mb-3">Slip Share</h1>

      <!-- Toolbar: the two actions, then a per-key summary of what needs work -->
      <div class="ss-toolbar">
        <div class="ss-toolbar__actions">
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx"
            class="d-none"
            @change="onFileChosen"
          />
          <b-button
            variant="primary"
            size="sm"
            :disabled="importing"
            :aria-busy="importing ? 'true' : 'false'"
            @click="$refs.fileInput.click()"
          >
            <b-spinner v-if="importing" small class="mr-1" label="" />
            <b-icon v-else icon="upload" class="mr-1" />
            {{ importing ? 'Importing…' : 'Import slip shares' }}
          </b-button>
          <b-dropdown
            class="ss-download"
            variant="outline-secondary"
            size="sm"
            :disabled="downloading"
            :aria-busy="downloading ? 'true' : 'false'"
          >
            <template v-slot:button-content>
              <b-spinner v-if="downloading" small class="mr-1" label="" />
              <b-icon v-else icon="download" class="mr-1" />
              {{ downloading ? 'Preparing template…' : 'Download template' }}
            </template>
            <b-dropdown-item @click="onDownloadTemplate('standard')">
              Standard template (.xlsx)
            </b-dropdown-item>
            <b-dropdown-item @click="onDownloadTemplate('fcp')">
              FCP Binder Splits template (.xlsx)
            </b-dropdown-item>
          </b-dropdown>
          <!-- An import already reloads the table, but the figures also move
               when someone edits the BDX List or another import runs - so a
               way to re-read without losing the filters is worth having. -->
          <b-button
            class="ss-refresh"
            variant="outline-secondary"
            size="sm"
            :disabled="importing || downloading"
            title="Reload the latest data"
            aria-label="Reload the latest data"
            @click="onRefresh"
          >
            <b-icon icon="arrow-clockwise" :class="{ 'ss-spin': refreshing }" />
          </b-button>
        </div>

        <div v-if="keyCounts.keys" class="ss-toolbar__summary">
          <span class="ss-pill ss-pill--complete">{{ keyCounts.complete }} complete</span>
          <span v-if="keyCounts.under" class="ss-pill ss-pill--under">{{ keyCounts.under }} under 100%</span>
          <span v-if="keyCounts.over" class="ss-pill ss-pill--over">{{ keyCounts.over }} over 100%</span>
          <span v-if="keyCounts.missing" class="ss-pill ss-pill--over">{{ keyCounts.missing }} missing an underwriter's share</span>
          <span v-if="keyCounts.exempt" class="ss-pill ss-pill--exempt">{{ keyCounts.exempt }} Hiscox-led</span>
          <span v-if="keyCounts.no_data" class="ss-pill ss-pill--none">{{ keyCounts.no_data }} no data</span>
        </div>
      </div>

      <b-alert v-if="loadError" variant="warning" show class="py-2">
        {{ loadError }}
      </b-alert>

      <!-- Rows come from the server; per-key totals and the Hiscox flag arrive
           on each row because a page cannot see all of a key's rows. -->
      <BaseDataTable
        ref="dataTableRef"
        :columns="columns"
        :items="itemsProvider"
        :total="total"
        :initial-page-size="10"
        :page-size-options="[10, 25, 50, 100]"
        :sticky-actions-column="false"
      >
        <template v-slot:cell(limeiq_key)="{ item }">
          <span class="ss-key">{{ item.limeiq_key }}</span>
        </template>

        <template v-slot:cell(umr)="{ item }">
          <span class="ss-mono">{{ item.umr }}</span>
        </template>

        <template v-slot:cell(lead)="{ item }">
          <span :class="item.lead ? 'font-weight-bold' : 'text-muted'">
            {{ item.lead ? 'Yes' : 'No' }}
          </span>
        </template>

        <!-- BaseDataTable forwards a cell() slot for EVERY column and renders
             nothing when the page supplies none - so a column without a template
             here shows a blank cell rather than its value. These two had none. -->
        <template v-slot:cell(risk_code)="{ item }">{{ item.risk_code || '—' }}</template>
        <template v-slot:cell(underwriter)="{ item }">{{ item.underwriter || '—' }}</template>

        <template v-slot:cell(syndicate_no)="{ item }">
          <span class="ss-mono">{{ item.syndicate_no || '—' }}</span>
        </template>

        <!-- Coloured by the whole key's total, not by this row: a single
             percentage is never right or wrong on its own. -->
        <template v-slot:cell(slip_share_pct)="{ item }">
          <span class="ss-pct" :class="pctClass(item)" :title="completeTitle(item)">
            {{ formatPct(item.slip_share_pct) }}
          </span>
        </template>

        <template v-slot:cell(lloyds)="{ item }">{{ item.lloyds ? 'Yes' : 'No' }}</template>
        <template v-slot:cell(fcp)="{ item }">{{ item.fcp ? 'Yes' : 'No' }}</template>

        <template v-slot:cell(broker)="{ item }">{{ item.broker || '—' }}</template>
        <template v-slot:cell(coverholder)="{ item }">{{ item.coverholder || '—' }}</template>
        <template v-slot:cell(tpa)="{ item }">{{ item.tpa || '—' }}</template>
        <!-- The server derives this: a Hiscox-led LIMOSS key reads LIMOSS (UW).
             Falls back to the raw value so a missing label never blanks the cell. -->
        <template v-slot:cell(billing_type)="{ item }">{{ item.billing_type_display || item.billing_type || '—' }}</template>

        <template v-slot:cell(complete)="{ item }">
          <span class="ss-flag" :class="pctClass(item)" :title="completeTitle(item)">
            {{ completeFlag(item) }}
          </span>
        </template>
      </BaseDataTable>

      <BaseModal
        v-model="showImportResultsModal"
        title="Slip Share Import Results"
        size="xl"
        scrollable
      >
        <p class="ss-import-summary">
          {{ importResultSummary.total }} row{{ importResultSummary.total === 1 ? '' : 's' }}:
          <span class="ss-flag ss--complete">{{ importResultSummary.imported }} Imported</span>,
          <span class="ss-flag ss--warning">{{ importResultSummary.warning }} Warning</span>,
          <span class="ss-flag ss--over">{{ importResultSummary.failed }} Failed</span>
        </p>
        <b-table
          :items="importResultRows"
          :fields="importResultFields"
          responsive
          show-empty
          empty-text="No rows to show."
        >
          <template v-slot:cell(status)="{ item }">
            <span
              class="ss-flag"
              :class="{
                'ss--over': item.status === 'Failed',
                'ss--warning': item.status === 'Warning',
                'ss--complete': item.status === 'Imported'
              }"
            >
              {{ item.status }}
            </span>
          </template>

          <!-- Wrapped rather than left to the table's default nowrap, so a
               long message reads in full instead of overflowing or being
               silently clipped. Only a Warning row carrying a key list gets
               the toggle - everything else is just the message text. -->
          <template v-slot:cell(message)="{ item }">
            <div class="ss-import-message">{{ item.message }}</div>
            <template v-if="item.keys && item.keys.length">
              <b-link class="ss-import-toggle" @click="toggleKeys(item)">
                <b-icon :icon="item.expanded ? 'chevron-down' : 'chevron-right'" class="mr-1" />
                {{ item.expanded ? 'Hide' : 'Show' }} all {{ item.keys.length }} keys
              </b-link>
              <b-collapse :visible="item.expanded">
                <ul class="ss-import-keys">
                  <li v-for="k in item.keys" :key="k" class="ss-mono">{{ k }}</li>
                </ul>
              </b-collapse>
            </template>
          </template>
        </b-table>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="secondary" @click="showImportResultsModal = false">Close</b-button>
        </div>
      </BaseModal>

    </b-container>
  </div>
</template>

<script>
import Vue from 'vue'
import { computed, defineComponent, onUnmounted, ref } from '@vue/composition-api'

import { BillingManagementService } from '@services/BillingManagementService'

import { BaseDataTable, BaseModal } from '@components/base'

import {
  getSlipShareTableColumns,
  SHARE_STATE,
  shareStateFor
} from '@components/slipshare/SlipShareTable.constants'

// Up to nine decimal places, because that is what the platform stores and
// the last is load-bearing: an equal three-way split is 33.333333333 x2 +
// 33.333333334, and rounding it away shows all three alike and their key
// short when it totals exactly 100. Trailing zeros come off, so a plain
// 40% share reads 40% rather than 40.000000000% - only a share actually
// entered to 9dp (40.938190471) shows all 9. Same rule everywhere this
// platform shows a share: BDX Edit's own underwriter label strips the
// same way.
function formatPct(value) {
  if (value === null || value === undefined) return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${n.toFixed(9).replace(/\.?0+$/, '')}%`
}

export default defineComponent({
  name: 'SlipShareService',

  components: {
    BaseDataTable,
    BaseModal
  },

  setup() {
    const columns = getSlipShareTableColumns()

    // Totals per LimeIQ key, in millionths (see the constants file).
    // Both come from the server on every row: a page cannot see all of a
    // key's rows, so neither the total nor the lead can be derived here.
    const stateOf = (item) => {
      const pct = item.key_total_pct
      // `|| 0` would turn a null into a real total and report a key nobody has
      // touched as under-100 - which is what made the two indistinguishable.
      // Scaled to a whole number before comparing: the business rule is exactly
      // 100 with no tolerance, and comparing floats for equality would make a
      // genuinely complete key flicker red.
      const units =
        (pct === null || pct === undefined) ? null : Math.round(pct * 1000000)
      return shareStateFor(units, !!item.hiscox_led, missingOf(item).length)
    }

    // Underwriters on the key holding no share at all - from the server,
    // because they may sit on another page.
    const missingOf = (item) => item.key_missing_underwriters || []

    // Counted by the server over every key in the filtered set - not just the
    // page - because a count sitting beside a 14k-row table has to mean the
    // table, not the twenty-five rows in front of you.
    const keyCounts = ref({
      complete: 0, under: 0, over: 0, missing: 0, exempt: 0, no_data: 0, keys: 0
    })

    const pctClass = (item) => `ss--${stateOf(item)}`

    // Neither Y nor N: the key is not being judged against the 100% rule, and
    // showing 'N' would read as a problem the user is expected to fix.
    const completeFlag = (item) => {
      const state = stateOf(item)
      if (state === SHARE_STATE.EXEMPT) return '—'
      return state === SHARE_STATE.COMPLETE ? 'Y' : 'N'
    }

    const completeTitle = (item) => {
      if (stateOf(item) === SHARE_STATE.EXEMPT) {
        return 'Hiscox-led: billed 100% to Hiscox, so the 100% rule does not apply'
      }
      if (stateOf(item) === SHARE_STATE.COMPLETE) return 'Shares total 100%'
      if (stateOf(item) === SHARE_STATE.NO_DATA) {
        return 'No slip share recorded for this key yet'
      }
      if (stateOf(item) === SHARE_STATE.MISSING) {
        const names = missingOf(item).join(', ')
        return `Shares total 100% but ${names} ${missingOf(item).length === 1 ? 'has' : 'have'} no slip share - every underwriter on the key needs one`
      }
      return `Shares total ${formatPct(item.key_total_pct)} - should be 100%`
    }

    const total = ref(0)
    const loadError = ref('')
    const dataTableRef = ref(null)
    // Purely for the spinning icon. handleRefresh() below doesn't return a
    // promise tied to the fetch completing, so this is a cosmetic minimum -
    // without it the icon would flash and the click would feel dead.
    const refreshing = ref(false)

    // handleRefresh() re-runs BaseDataTable's own provider call in place
    // (bootstrap-vue's native b-table.refresh()) rather than remounting the
    // component - the earlier :key-bump approach reloaded the data but also
    // reset every column filter, the search box and the current page, since
    // remounting throws away BaseDataTableTable's internal state along with
    // the old instance. refresh() keeps all of that exactly as the user left
    // it and just asks the server for the same query again.
    const refreshTable = () => {
      if (dataTableRef.value) dataTableRef.value.handleRefresh()
    }

    const onRefresh = () => {
      if (refreshing.value) return
      refreshing.value = true
      refreshTable()
      setTimeout(() => { refreshing.value = false }, 600)
    }

    // What the table is currently narrowed to. Drives the download, so the file
    // and the screen cannot disagree.
    const activeFilters = ref({})
    const isFiltered = computed(() =>
      Object.values(activeFilters.value).some(
        (v) => v !== undefined && v !== null && String(v).trim() !== ''
      )
    )

    /**
     * Server-backed. Filtering, sorting and paging all happen in the database -
     * the table holds ~15k rows, so pulling them all to filter in the browser
     * would be slow and would still get the per-key totals wrong, because a key
     * can straddle a page boundary.
     */
    const itemsProvider = async (context) => {
      const { filter, currentPage, perPage, sortBy, sortDesc, filters } =
        context || {}
      loadError.value = ''

      // Per-column filters arrive as [{key, value}] from BaseDataTableTable and
      // are separate from `filter`, which is the toolbar search. Forwarding them
      // is what makes the column filter chips actually filter - without this the
      // chip shows but the query is unfiltered.
      const columnFilters = {}
      ;(filters || []).forEach((f) => {
        if (f && f.key && f.value !== null && f.value !== undefined && f.value !== '') {
          columnFilters[f.key] = f.value
        }
      })

      // Kept so the template download can ask for exactly what the table is
      // showing. Captured here, from the object the query is actually built
      // from, rather than re-read off the table afterwards - anything derived a
      // second way can disagree with what the user is looking at, which is the
      // one thing this feature must not do. Set before the request, so a failed
      // load still leaves the filters that were asked for.
      activeFilters.value = { search: filter || undefined, ...columnFilters }

      try {
        const res = await BillingManagementService.fetchSlipShareRows({
          page: currentPage || 1,
          page_size: perPage || 25,
          ...activeFilters.value,
          sort_by: sortBy || undefined,
          sort_desc: sortDesc ? 'true' : undefined,
          // Only on the first page: the aggregate is the slow part and it does
          // not change while paging. b-table resets to page 1 whenever the
          // filter changes, so this refreshes exactly when it needs to.
          summary: (currentPage || 1) === 1 ? '1' : undefined
        })
        const body = (res && res.data && res.data.data) || (res && res.data) || {}
        total.value = body.count || 0
        if (body.summary) keyCounts.value = body.summary
        return body.results || []
      } catch (err) {
        loadError.value = 'Could not load slip share data.'
        total.value = 0
        return []
      }
    }

    // Toasts are registered globally with a three second timeout, which is not
    // long enough to read a row reference off an import error, and nothing lets
    // the user hold one open. Upload errors get their own treatment - no
    // timeout, plus a Dismiss control - while every other toast on this page
    // keeps the global behaviour.
    let errorToast = null

    const showUploadError = (message) => {
      const toast = Vue.prototype.$toasted.error(message, {
        duration: null,
        action: {
          text: 'Dismiss',
          onClick: (event, toastObject) => {
            toastObject.goAway(0)
            errorToast = null
          }
        }
      })
      errorToast = toast

      // The container the library creates is already role=status/aria-live, so
      // the message itself is announced; only the control needs work.
      const el = toast && toast.el
      const action = el && el.querySelector('.action')
      if (!action) return
      // The action is built as a bare <a> with no href: it takes no focus and
      // fires no click from the keyboard. Both have to be added here, because
      // the library exposes no way to set attributes on it.
      action.setAttribute('role', 'button')
      action.setAttribute('tabindex', '0')
      action.setAttribute('aria-label', 'Dismiss notification')
      action.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        action.click()
      })
    }

    // Without a timeout the toast outlives the page - it lives on the body, not
    // in this component - so leaving would carry it onto an unrelated screen.
    onUnmounted(() => {
      if (errorToast) errorToast.goAway(0)
      errorToast = null
    })

    const importing = ref(false)

    // Full breakdown of the last import, shown in a modal instead of the
    // handful of lines a toast can hold - the backend now returns every row's
    // outcome, not just a capped errors list, so there is a full list to show.
    const showImportResultsModal = ref(false)
    const importResultRows = ref([])
    const importResultFields = [
      { key: 'status', label: 'Status', thStyle: { width: '110px' } },
      { key: 'message', label: 'Detail' }
    ]

    // A plain count of what's already in importResultRows - not a separate
    // figure from the server, so it can never disagree with the list below
    // it. Only Imported/Warning/Failed are counted; Valid-vs-Pending-Fix and
    // per-error-type grouping aren't derivable from this list (both need
    // state this response doesn't carry) and are deliberately left out
    // rather than guessed at.
    const importResultSummary = computed(() => {
      const rows = importResultRows.value
      return {
        total: rows.length,
        imported: rows.filter((r) => r.status === 'Imported').length,
        warning: rows.filter((r) => r.status === 'Warning').length,
        failed: rows.filter((r) => r.status === 'Failed').length
      }
    })

    // Mutates an existing reactive property (see the comment where warning
    // rows are built) rather than replacing the array, so b-table doesn't
    // need to re-render every row just to toggle one.
    const toggleKeys = (item) => {
      item.expanded = !item.expanded
    }

    const onFileChosen = async (event) => {
      const file = event.target.files && event.target.files[0]
      // Reset immediately, or choosing the same file twice fires no change event
      // and the second import silently never happens.
      event.target.value = ''
      if (!file || importing.value) return

      importing.value = true
      try {
        const res = await BillingManagementService.importSlipShares(file)
        const body = (res && res.data && res.data.data) || (res && res.data) || {}
        const changed = (body.created || 0) + (body.updated || 0)
        Vue.prototype.$toasted.show(
          `Imported ${changed} row${changed === 1 ? '' : 's'}` +
            (body.skipped ? `, ${body.skipped} skipped` : '')
        )
        // Failures first - the ones that need attention - then warnings - rows
        // that DID write, but are worth a second look - then plain successes,
        // so a long file does not bury the one row someone is looking for.
        const errorRows = (body.errors || []).map((message) => ({ status: 'Failed', message }))
        // expanded starts false and lives on the row object from the start, so
        // toggling it later is a plain mutation of an existing reactive
        // property - adding the key fresh at toggle time is the classic Vue 2
        // trap where the new property never becomes reactive.
        const warningRows = (body.warnings || []).map((w) => ({
          status: 'Warning',
          message: w.message,
          keys: w.keys || null,
          expanded: false
        }))
        const successRows = (body.successes || []).map((message) => ({ status: 'Imported', message }))
        if (errorRows.length || warningRows.length || successRows.length) {
          importResultRows.value = [...errorRows, ...warningRows, ...successRows]
          showImportResultsModal.value = true
        }
        refreshTable()
      } catch (err) {
        const body =
          (err && err.response && err.response.data && err.response.data.data) ||
          (err && err.response && err.response.data) || {}
        showUploadError(body.error || 'The file could not be imported.')
      } finally {
        importing.value = false
      }
    }

    const downloading = ref(false)

    const onDownloadTemplate = async (kind) => {
      if (downloading.value) return
      downloading.value = true
      try {
        // The FCP template is a fixed, blank layout - no filters, and no
        // "filtered file" toast, since there is no existing data it could
        // have been filtered down from.
        const res = kind === 'fcp'
          ? await BillingManagementService.downloadFcpBinderSplitsTemplate()
          : await BillingManagementService.downloadSlipShareTemplate(activeFilters.value)
        // The response is a blob, so an object URL is the only way to hand it to
        // the browser as a file - there is no URL the user could navigate to,
        // because the request carries their auth header.
        // Typed blob: an untyped one defaults to '' and some browsers then
        // treat the download as unknown-content and prompt harder.
        const url = window.URL.createObjectURL(
          new Blob([res.data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
        )
        const link = document.createElement('a')
        link.href = url
        // The download attribute is what actually names the saved file - the
        // server sends a matching Content-Disposition, but it is cross-origin
        // and not exposed to JS, so the name is built here. The browser's own
        // date rather than the server's: the user means their today, and the
        // server runs on America/Los_Angeles.
        const today = new Date()
        const stamp = [
          today.getFullYear(),
          String(today.getMonth() + 1).padStart(2, '0'),
          String(today.getDate()).padStart(2, '0')
        ].join('-')
        const filePrefix = kind === 'fcp' ? 'fcp-binder-splits-template' : 'slip-share-template'
        link.setAttribute('download', `${filePrefix}-${stamp}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        // Say so, or a short file reads as missing data rather than as the
        // filter the user applied themselves.
        Vue.prototype.$toasted.show(
          kind === 'fcp'
            ? 'Template downloaded.'
            : (isFiltered.value
              ? 'Template downloaded for the filtered rows only.'
              : 'Template downloaded.')
        )
      } catch (err) {
        Vue.prototype.$toasted.error(
          'Could not download the template. Please try again.'
        )
      } finally {
        downloading.value = false
      }
    }

    return {
      columns,
      total,
      loadError,
      itemsProvider,
      keyCounts,
      stateOf,
      pctClass,
      completeFlag,
      completeTitle,
      formatPct,
      SHARE_STATE,
      onDownloadTemplate,
      onFileChosen,
      importing,
      showImportResultsModal,
      importResultRows,
      importResultFields,
      importResultSummary,
      toggleKeys,
      dataTableRef,
      refreshing,
      onRefresh,
      downloading
    }
  }
})
</script>

<style lang="scss" scoped>
/* Only while refreshing, and only if the reader has not asked for stillness. */
@keyframes ss-spin { to { transform: rotate(360deg); } }
.ss-spin { animation: ss-spin .6s linear; transform-origin: 50% 50%; }
@media (prefers-reduced-motion: reduce) { .ss-spin { animation: none; } }

// BaseDataTable wraps itself in its own <b-container class="py-4"> plus a card.
// Left alone that double-pads the page and, because the inner container isn't
// fluid, caps a 13-column table at the default max-width. Same override the
// billing tables use.
.ss-root ::v-deep .container {
  padding: 0 !important;
  max-width: initial !important;
}

.ss-root ::v-deep .card .card-body {
  padding: 0 1.5rem !important;
}

.ss-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  // Row gap matters once this wraps on a narrow viewport; column gap keeps the
  // actions clear of the summary pills.
  gap: 0.5rem 1rem;
  margin-bottom: 0.75rem;
}

.ss-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ss-toolbar__summary {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.ss-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.ss-pill--complete { background: #e6f4ea; color: #1b7a4b; }
.ss-pill--under    { background: #eef1f2; color: #525f7f; }
.ss-pill--over     { background: #fdecea; color: #dc3545; }
// Hiscox-led keys are exempt from the 100% rule, not passing it - a distinct
// colour so they are not read as either green or red.
.ss-pill--exempt   { background: #eceff3; color: #4a5568; }
// Most of the table today. Muted on purpose: nothing is wrong with these keys,
// nobody has supplied shares for them yet.
.ss-pill--none     { background: #f4f5f7; color: #8898aa; }

.ss-key  { color: #1c4a50; font-weight: 600; font-size: 11px; }
.ss-mono { font-family: monospace; font-size: 11px; }

.ss-pct  { font-weight: 600; }
.ss-flag { font-weight: 700; }

/* Scoped to this page only - BaseDataTable is shared with other screens,
   so this is set here via a deep selector rather than in that component,
   where it would change every table that uses it. ::v-deep to match the
   existing .container override just above (two rules resolving to the
   same selector via different deep-combinator syntax - >>> vs ::v-deep -
   previously corrupted cssnano's minification pass and silently dropped
   every rule after the duplicate; keeping one syntax throughout avoids
   that entirely).

   BaseDataTableTable.vue turns on sticky-header, which caps the table at
   max-height: 550px and scrolls the rows inside that fixed box - the
   vertical scrollbar this removes. Lifting the cap lets the rows render
   at full height and the page scroll normally instead of a boxed-in
   scroll region. */
.ss-root ::v-deep .b-table-sticky-header {
  max-height: none;
  overflow-y: visible;
}

// Shared by the percentage and the Y/N flag so a key reads consistently across
// both columns.
/* White, per the story - deliberately overrides the red/green formatting.
   Paired with a background so it stays legible rather than invisible. */
/* Bootstrap fades a disabled button to .65 opacity, which reads as broken
   rather than busy. While downloading the button keeps its weight and simply
   stops responding - the spinner carries the state. */
.ss-download:disabled,
.ss-download.disabled {
  opacity: 1;
  cursor: progress;
}

/* The menu otherwise sizes to the toggle button rather than to its own
   items - fine for "Standard template (.xlsx)" but it clips "FCP Binder
   Splits template (.xlsx)" since that label is longer than the button.
   Sized to content explicitly instead of relying on the default.
   ::v-deep (not a plain descendant selector): b-dropdown renders the menu
   deep inside its own template, not at its root, so scoped CSS's data-v
   attribute never reaches it. ExportLog.vue uses the equivalent >>> for
   the same reason, but that file's <style> block is plain CSS - >>> isn't
   valid SCSS and is silently dropped by sass-loader before vue-loader ever
   sees it, so a scss-flavoured block needs ::v-deep instead. */
.ss-download ::v-deep .dropdown-menu {
  width: max-content;
  min-width: 100%;
}

.ss--exempt   { color: #ffffff; background: #6b7c93; padding: 0 6px;
                border-radius: 3px; }
.ss--complete { color: #1b7a4b; }
/* Under and over are both 'supplied, and does not total 100', so both read red.
   They stay distinguishable through the tooltip, which quotes the actual total -
   colour only has to say whether something needs fixing. */
.ss--under    { color: #dc3545; }
.ss--over     { color: #dc3545; }
/* Totals 100 but an underwriter has no share - just as wrong, so also red. */
.ss--missing  { color: #dc3545; }
/* Grey, and only for a key nobody has supplied shares for. This grey was
   previously also used for under-100, which made a real error look like an
   untouched key. */
.ss--nodata   { color: #8898aa; }
/* A row that wrote successfully but is worth a second look - distinct from
   both the red of a real failure and the green of a plain success, same
   amber the rest of the app uses for an attention flag (PendingInvoicesTable's
   missing-data flag). */
.ss--warning  { color: #e67e22; }

/* Import results modal: the one-line total/status count above the table. */
.ss-import-summary {
  font-size: 14px;
  color: #525f7f;
  margin-bottom: 12px;
}
.ss-import-summary .ss-flag {
  font-size: 14px;
}

/* Import results modal: message column wraps instead of the table's default
   nowrap, which would either overflow or silently clip a long message. */
.ss-import-message {
  white-space: normal;
  word-break: break-word;
}
.ss-import-toggle {
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
}
.ss-import-keys {
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: 12px;
}
.ss-import-keys li {
  margin-bottom: 2px;
}
</style>
